import { addClassNamesToElement } from '@lexical/utils'
import {
  $applyNodeReplacement,
  DecoratorNode,
  type DOMConversionMap,
  type DOMExportOutput,
  type EditorConfig,
  type LexicalNode,
  type NodeKey
} from 'lexical'

import { downloadFileApi } from '@eastcoal/api/nexus/oss/downloadFile'

import { system } from '@eastcoal/common/config/system'
import { findParentElement } from '@eastcoal/common/utils/dom'
import { isEmpty } from '@eastcoal/common/utils/object'
import { stringifyUrl } from '@eastcoal/common/utils/querystring'
import { isExternalUrl } from '@eastcoal/common/utils/url'

import { ImageCard } from '../../ui/ImageCard'
import { initialImageCardStore } from '../../ui/ImageCard/Store/store/createStore'
import {
  IMAGE_BORDER_STYLE,
  IMAGE_SHADOW_STYLE
} from '../../ui/ImageCard/utils/style'

import {
  ImageNodeProps,
  ImageNodeState,
  SerializedImageNode
} from './interface'

const isAnchorElement = (target: HTMLElement): target is HTMLAnchorElement => {
  const caseInsensitiveTagName = target.tagName.toUpperCase()
  return caseInsensitiveTagName === 'A'
}

const resolveImageLink = (el: HTMLImageElement) => {
  const anchor = findParentElement({
    target: el,
    $predicate: isAnchorElement
  })
  return anchor?.href
}

export class ImageNode extends DecoratorNode<React.JSX.Element> {
  private __file?: Blob | string
  private __data: ImageNodeState

  constructor({ file, state }: ImageNodeProps, key?: NodeKey) {
    super(key)
    this.__file = file
    this.__data = {
      ...initialImageCardStore,
      ...state
    }
  }

  getKey() {
    return this.__key
  }

  getState() {
    return this.__data
  }

  setState(state: Partial<ImageNodeState>) {
    // 请求完成后才能更新状态
    if (state.ossId || state.error) {
      const writable = this.getWritable()
      writable.__data = {
        ...writable.__data,
        ...state
      }
    }
  }

  static getType() {
    return 'image'
  }

  /**
   * 每次状态变更时都会调用，所以不能传 file 防止 ImageCard 里面重复上传
   */
  static clone(node: ImageNode) {
    return new ImageNode(
      {
        state: node.__data
      },
      node.getKey()
    )
  }

  exportJSON(): SerializedImageNode {
    return {
      ...super.exportJSON(),
      type: ImageNode.getType(),
      state: this.getState()
    }
  }

  static importJSON(_serializedNode: SerializedImageNode) {
    return $createImageNode(_serializedNode)
  }

  exportDOM(): DOMExportOutput {
    const {
      ossId,
      rotate,
      bordered,
      shadowed,
      link,
      desc,
      currentHeight,
      currentWidth
    } = this.__data
    if (!ossId) {
      return { element: null }
    }

    const src = stringifyUrl({
      url: system.api.baseURL + downloadFileApi.url,
      query: {
        ossId: ossId,
        imageProcess: {
          rotate: rotate,
          resize: {
            m: 'fixed',
            h: currentHeight,
            w: currentWidth
          }
        }
      }
    })
    const img = document.createElement('img')
    img.src = src
    if (bordered) {
      img.style.border = IMAGE_BORDER_STYLE
    }
    if (shadowed) {
      img.style.boxShadow = IMAGE_SHADOW_STYLE
    }
    if (desc) {
      img.alt = desc
    }

    if (link) {
      const a = document.createElement('a')
      a.href = link
      a.target = '_blank'
      a.style.display = 'inline-block'
      a.appendChild(img)
      return { element: a }
    }

    return { element: img }
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: () => ({
        priority: 0,
        conversion(dom) {
          if (!(dom instanceof HTMLImageElement) || isEmpty(dom.src.trim())) {
            return null
          }
          const file = dom.src
          let link = resolveImageLink(dom)
          if (!isExternalUrl(link)) {
            link = ''
          }
          const bordered = !!dom.style.border
          const shadowed = !!dom.style.boxShadow
          const node = $createImageNode({
            file,
            state: {
              link,
              bordered,
              shadowed,
              currentHeight: dom.height,
              currentWidth: dom.width
            }
          })
          return { node }
        }
      })
    }
  }

  createDOM(config: EditorConfig): HTMLElement {
    const div = document.createElement('div')
    addClassNamesToElement(div, config.theme.image)
    return div
  }

  updateDOM() {
    return false
  }

  isInline() {
    return true
  }

  isIsolated() {
    return false
  }

  decorate(): React.JSX.Element {
    return (
      <ImageCard file={this.__file} nodeKey={this.getKey()} {...this.__data} />
    )
  }
}

export const $createImageNode = (props: ImageNodeProps) => {
  return $applyNodeReplacement(new ImageNode(props))
}

export function $isImageNode(
  node: LexicalNode | null | undefined
): node is ImageNode {
  return node instanceof ImageNode
}
